import { SidebarIcon } from 'lucide-react';

import { Button, Separator, useSidebar } from '@data-delivery/d3s';
import { SearchCmdk } from '../../components';
import { ThemeSwitcher } from '../../themes/theme-switcher';
import { BrandLogo } from './brand-logo/brand-logo';
import { DynamicBreadcrumb } from './dynamic-breadcrumb';
import { navigationItems } from './navigation-config';
import type { MenuItem } from './nav-main';

/**
 * Builds a path → linkState lookup from the navigation tree.
 * Any MenuItem (or sub-item) that declares `linkState` will be included,
 * so new modules never require changes here — only in navigation-config.tsx.
 */
function buildBreadcrumbLinkStateMap(
  items: MenuItem[],
): Record<string, Record<string, unknown>> {
  const map: Record<string, Record<string, unknown>> = {};
  for (const item of items) {
    if (item.linkState && item.url !== '#') {
      map[item.url] = item.linkState;
    }

    for (const sub of item.items ?? []) {
      if (sub.linkState) {
        map[sub.url] = sub.linkState;
      }
    }
  }

  return map;
}

const breadcrumbLinkStateMap = buildBreadcrumbLinkStateMap(navigationItems);

export function SiteHeader() {
  const { toggleSidebar } = useSidebar();

  return (
    <header className='flex sticky top-0 z-50 w-full items-center bg-sidebar text-sidebar-foreground'>
      <div className='flex h-[--header-height] w-full items-center gap-2 px-4'>
        <BrandLogo
          text='PC SFTP Permissioning UI'
          logoClassName='text-black w-[110px] dark:text-white'
          textClassName='text-sm font-medium text-black dark:text-white mr-2'
        />
        <Button className='h-8 w-8' variant='ghost' size='icon' onClick={toggleSidebar}>
          <SidebarIcon />
        </Button>
        <Separator orientation='vertical' className='mr-2 h-4' />
        <DynamicBreadcrumb
          className='hidden sm:block flex-1 min-w-0'
          navigationItems={navigationItems}
          options={{
            getLinkState: crumb => breadcrumbLinkStateMap[crumb.path],
          }}
        />
        <SearchCmdk className='ml-auto mr-3' />
        <ThemeSwitcher />
      </div>
    </header>
  );
}
